/**
 * Audio player utility functions
 */

import type { WaveformData } from '../types'
import type { TimeRange } from '../../types/base'

/**
 * Formats seconds into a time string (mm:ss or hh:mm:ss).
 *
 * @param seconds - The time in seconds to format
 * @returns Formatted time string
 *
 * @example
 * ```ts
 * formatTime(65) // "1:05"
 * formatTime(3661) // "1:01:01"
 * ```
 */
export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00'
  }

  const totalSeconds = Math.floor(seconds)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * Parses waveform data from various formats.
 *
 * @param data - Raw waveform data (ArrayBuffer, number array, or JSON object)
 * @returns Parsed WaveformData object
 *
 * @example
 * ```ts
 * const waveform = parseWaveformData({ peaks: [0.1, 0.5, 0.8], sample_rate: 44100, duration: 120 })
 * ```
 */
export function parseWaveformData(
  data:
    | ArrayBuffer
    | number[]
    | { peaks: number[]; sample_rate?: number; sampleRate?: number; duration: number }
): WaveformData {
  // audiowaveform binary (.dat) format: 24-byte header + 8-bit min/max pairs
  if (data instanceof ArrayBuffer) {
    const headerView = new DataView(data)
    const version = headerView.getInt32(0, true)

    // Detect audiowaveform .dat format (version 1 or 2)
    if (version === 1 || version === 2) {
      const sampleRate = headerView.getInt32(8, true)
      const samplesPerPixel = headerView.getInt32(12, true)
      const length = headerView.getUint32(16, true)
      const headerSize = version === 2 ? 24 : 20
      const samples = new Int8Array(data, headerSize)

      const peaks: number[] = []
      for (let i = 0; i < samples.length; i += 2) {
        const absMin = Math.abs(samples[i] ?? 0)
        const absMax = Math.abs(samples[i + 1] ?? 0)
        peaks.push(Math.max(absMin, absMax) / 128)
      }

      return {
        peaks,
        sampleRate,
        duration: (length * samplesPerPixel) / sampleRate,
      }
    }

    // Fallback: treat as raw Float32Array
    const view = new Float32Array(data)
    return {
      peaks: Array.from(view),
      sampleRate: 44100,
      duration: view.length / 44100,
    }
  }

  if (Array.isArray(data)) {
    return {
      peaks: data,
      sampleRate: 44100,
      duration: data.length / 100,
    }
  }

  return {
    peaks: data.peaks,
    sampleRate: data.sample_rate ?? data.sampleRate ?? 44100,
    duration: data.duration,
  }
}

/**
 * Extracts buffered time ranges from an HTMLAudioElement.
 *
 * @param audio - The HTMLAudioElement to extract buffered ranges from
 * @returns Array of TimeRange objects representing buffered portions
 *
 * @example
 * ```ts
 * const ranges = calculateBufferedRanges(audioElement)
 * // [{ start: 0, end: 30 }, { start: 45, end: 60 }]
 * ```
 */
export function calculateBufferedRanges(audio: HTMLAudioElement): TimeRange[] {
  const ranges: TimeRange[] = []
  const buffered = audio.buffered

  for (let i = 0; i < buffered.length; i++) {
    ranges.push({
      start: buffered.start(i),
      end: buffered.end(i),
    })
  }

  return ranges
}

/**
 * Calculates the total buffered duration as a percentage.
 *
 * @param ranges - Array of buffered time ranges
 * @param duration - Total duration of the audio
 * @returns Buffered percentage (0-100)
 */
export function calculateBufferedPercentage(ranges: TimeRange[], duration: number): number {
  if (duration <= 0) return 0

  const totalBuffered = ranges.reduce((sum, range) => sum + (range.end - range.start), 0)
  return Math.min(100, (totalBuffered / duration) * 100)
}

/**
 * Clamps a value between min and max.
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Formats playback speed for display.
 *
 * @param speed - Playback speed multiplier
 * @returns Formatted speed string
 *
 * @example
 * ```ts
 * formatPlaybackSpeed(1) // "1x"
 * formatPlaybackSpeed(1.5) // "1.5x"
 * ```
 */
export function formatPlaybackSpeed(speed: number): string {
  return `${speed}x`
}

/**
 * Formats volume as a percentage.
 *
 * @param volume - Volume level (0-1)
 * @returns Formatted percentage string
 */
export function formatVolume(volume: number): string {
  return `${Math.round(volume * 100)}%`
}

/**
 * Default playback speeds available in the audio player.
 */
export const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const

export type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number]

/**
 * Generates waveform peaks from audio data (simplified version).
 * For production, use a Web Worker and more sophisticated analysis.
 *
 * @param audioBuffer - The AudioBuffer to analyze
 * @param numBars - Number of bars to generate
 * @returns Array of normalized peak values (0-1)
 */
export function generateWaveformPeaks(audioBuffer: AudioBuffer, numBars: number = 100): number[] {
  const channelData = audioBuffer.getChannelData(0)
  const blockSize = Math.floor(channelData.length / numBars)
  const peaks: number[] = []

  for (let i = 0; i < numBars; i++) {
    const start = i * blockSize
    const end = Math.min(start + blockSize, channelData.length)

    let max = 0
    for (let j = start; j < end; j++) {
      const absValue = Math.abs(channelData[j] ?? 0)
      if (absValue > max) {
        max = absValue
      }
    }
    peaks.push(max)
  }

  // Normalize peaks to 0-1 range
  const maxPeak = Math.max(...peaks)
  if (maxPeak > 0) {
    return peaks.map((peak) => peak / maxPeak)
  }

  return peaks
}

/**
 * Converts a CSS hex color to an RGB tuple.
 * Supports #RGB, #RRGGBB and #RRGGBBAA (the alpha is dropped); named CSS colors are ignored
 * (returns null).
 */
export function hexToRgb(hex: string): [number, number, number] | null {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})(?:[a-f\d]{2})?$/i.exec(hex)
  if (match) {
    const [, r = '', g = '', b = ''] = match
    return [parseInt(r, 16), parseInt(g, 16), parseInt(b, 16)]
  }
  const shortMatch = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex)
  if (shortMatch) {
    const [, r = '', g = '', b = ''] = shortMatch
    return [parseInt(r + r, 16), parseInt(g + g, 16), parseInt(b + b, 16)]
  }
  return null
}

/**
 * The one colour the audio player paints its accents and its icons with, or `undefined` to let it
 * sample the cover art.
 *
 * **`iconColor` wins wherever it is set, then `primaryColor`.** `iconColor` is the audio player's
 * own field — the one `sites`' `PlayerConfig` doc names as *"the same colour, for the audio
 * player"* — so a publication that sets it has spoken about this player specifically. Failing it,
 * the audio player follows `primaryColor` as the video, image and document players do: the mounted
 * option first (what the page or the embed's resolved look asked for), then the theme's (a preset,
 * or the platform's colour beneath it).
 */
export function resolveAudioAccentColor(sources: {
  iconColor?: string | null
  configIconColor?: string | null
  primaryColor?: string | null
  themePrimaryColor?: string | null
}): string | undefined {
  return (
    sources.iconColor ||
    sources.configIconColor ||
    sources.primaryColor ||
    sources.themePrimaryColor ||
    undefined
  )
}
